import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash2, ClipboardList } from 'lucide-react';

const EMPTY = {
  agency_name: '', internship_title: '', application_date: '', deadline: '',
  contact_person: '', contact_info: '', resume_submitted: false, cover_letter_submitted: false,
  interview_date: '', followup_date: '', notes: '', status: 'Interested',
};

const STATUS_COLORS = {
  'Interested': 'bg-gray-100 text-gray-600', 'Preparing Application': 'bg-blue-100 text-blue-700',
  'Applied': 'bg-indigo-100 text-indigo-700', 'Interview Scheduled': 'bg-amber-100 text-amber-700',
  'Waiting for Response': 'bg-purple-100 text-purple-700', 'Accepted': 'bg-green-100 text-green-700',
  'Declined': 'bg-red-100 text-red-700', 'Withdrawn': 'bg-gray-200 text-gray-500',
  'Placement Confirmed': 'bg-emerald-600 text-white',
};

const PortalApplications = ({ meta }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API_URL}/internship-portal/applications`); setApps(res.data.applications || []); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a.app_id); setForm({ ...EMPTY, ...a }); setOpen(true); };

  const save = async () => {
    if (!form.agency_name && !form.internship_title) { toast.error('Add an agency or internship title.'); return; }
    setSaving(true);
    try {
      if (editing) await axios.put(`${API_URL}/internship-portal/applications/${editing}`, form);
      else await axios.post(`${API_URL}/internship-portal/applications`, form);
      toast.success(editing ? 'Updated.' : 'Added to tracker.');
      setOpen(false); await load();
    } catch (e) { toast.error('Could not save.'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this application from your tracker?')) return;
    try { await axios.delete(`${API_URL}/internship-portal/applications/${id}`); await load(); toast.success('Removed.'); }
    catch (e) { toast.error('Could not remove.'); }
  };

  if (loading) return <div className="py-16 text-center text-gray-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="portal-applications">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-lg font-bold text-primary flex items-center gap-2"><ClipboardList className="w-5 h-5 text-secondary" /> My Application Tracker</h3>
        <Button size="sm" className="bg-secondary text-white" onClick={openNew} data-testid="add-application-btn"><Plus className="w-4 h-4 mr-1" /> Add Application</Button>
      </div>
      <p className="text-sm text-gray-500">Privately record and track every internship you're interested in or applying to. Only you can see this.</p>

      {apps.length === 0 ? (
        <div className="bg-white rounded-2xl border p-10 text-center text-gray-500" data-testid="no-applications">No applications tracked yet. Add one, or use "Save to My Internship Tracker" on any internship listing.</div>
      ) : (
        <div className="grid gap-3">
          {apps.map((a) => (
            <div key={a.app_id} className="bg-white rounded-2xl border p-5" data-testid={`application-${a.app_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h4 className="font-serif text-lg font-bold text-primary">{a.internship_title || 'Internship'}</h4>
                  <p className="text-sm text-gray-600">{a.agency_name}</p>
                </div>
                <Badge className={STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-600'}>{a.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 mt-3">
                {a.deadline && <span>Deadline: <strong className="text-gray-700">{a.deadline}</strong></span>}
                {a.interview_date && <span>Interview: <strong className="text-gray-700">{a.interview_date}</strong></span>}
                {a.contact_person && <span>Contact: <strong className="text-gray-700">{a.contact_person}</strong></span>}
                {a.resume_submitted && <span className="text-green-700">Resume ✓</span>}
                {a.cover_letter_submitted && <span className="text-green-700">Cover letter ✓</span>}
              </div>
              {a.notes && <p className="text-sm text-gray-600 mt-2">{a.notes}</p>}
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`edit-application-${a.app_id}`}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(a.app_id)} data-testid={`delete-application-${a.app_id}`}><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Application' : 'Add Application'}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Agency Name"><Input value={form.agency_name} onChange={(e) => setF('agency_name', e.target.value)} data-testid="app-agency" /></Field>
            <Field label="Internship Title"><Input value={form.internship_title} onChange={(e) => setF('internship_title', e.target.value)} data-testid="app-title" /></Field>
            <Field label="Application Date"><Input type="date" value={form.application_date} onChange={(e) => setF('application_date', e.target.value)} /></Field>
            <Field label="Application Deadline"><Input type="date" value={form.deadline} onChange={(e) => setF('deadline', e.target.value)} /></Field>
            <Field label="Contact Person"><Input value={form.contact_person} onChange={(e) => setF('contact_person', e.target.value)} /></Field>
            <Field label="Contact Info"><Input value={form.contact_info} onChange={(e) => setF('contact_info', e.target.value)} /></Field>
            <Field label="Interview Date"><Input type="date" value={form.interview_date} onChange={(e) => setF('interview_date', e.target.value)} /></Field>
            <Field label="Follow-up Date"><Input type="date" value={form.followup_date} onChange={(e) => setF('followup_date', e.target.value)} /></Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setF('status', v)}>
                <SelectTrigger data-testid="app-status"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta.application_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="flex gap-6 mt-1">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><Checkbox checked={form.resume_submitted} onCheckedChange={(v) => setF('resume_submitted', !!v)} data-testid="app-resume" /> Resume submitted</label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><Checkbox checked={form.cover_letter_submitted} onCheckedChange={(v) => setF('cover_letter_submitted', !!v)} data-testid="app-cover" /> Cover letter submitted</label>
          </div>
          <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setF('notes', e.target.value)} data-testid="app-notes" /></Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-primary text-white" onClick={save} disabled={saving} data-testid="save-application-btn">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div><Label className="text-xs text-gray-500 mb-1 block">{label}</Label>{children}</div>
);

export default PortalApplications;
